/**
 * The API's error envelope, shared so the dashboard can branch on `code`
 * rather than parsing English.
 */

export const ERROR_CODES = [
  'validation_error',
  'unauthorized',
  'invalid_credentials',
  'token_expired',
  'token_reused',
  'forbidden',
  'not_found',
  'conflict',
  'rate_limited',
  'payload_too_large',
  'unsupported_media_type',
  'internal_error',
  // A dependency CenoX does not control is down. Distinct from
  // `internal_error`, which means CenoX itself is broken — the dashboard shows
  // "try again shortly" for one and "we have been notified" for the other.
  'service_unavailable',
  // A feature this deployment does not have configured at all. Distinct from
  // `service_unavailable`, which means a dependency is down and retrying is
  // worth it — this one will not change until somebody configures it.
  'not_implemented',
  /**
   * An operational record was written without saying where it happened.
   *
   * Its own code rather than a `validation_error` because the dashboard can act
   * on it: the working location is already in the app shell, so a client seeing
   * this can retry with it instead of showing the user a form error about a
   * field they never filled in.
   */
  'location_required',
  /** The AI assistant has no key to use: no platform key, and none saved for this business. */
  'ai_key_missing',
  /** The business's own AI provider key was rejected by the provider. */
  'ai_key_invalid',
  /** The business's own AI provider account is out of credit or over its quota. */
  'ai_quota_exceeded',
  /** CenoX-managed AI is not part of this plan (bring your own key instead). */
  'ai_managed_unavailable',
  /** Included AI credit and top-up balance are both used up for this cycle. */
  'ai_credit_exhausted',
  /** A daily or monthly AI spending ceiling has been reached. */
  'ai_spend_cap_reached',
  /** One request would cost more than the per-request ceiling allows. */
  'ai_request_too_large',
  /**
   * The day's AI token ceiling is reached.
   *
   * Separate from `ai_spend_cap_reached` because they are different answers: a
   * spend cap means the request would cost more than is allowed, and this
   * means enough has been consumed for today — whatever it cost, including
   * nothing when the business uses its own provider key.
   */
  'ai_token_cap_reached',
  /**
   * The same request (same idempotency key) is still being answered. Send it
   * again shortly: once ready it is returned as it was, and charged once.
   */
  'ai_request_in_progress',
  'ai_search_limit_reached',
  'ai_search_unavailable',
  'ai_search_in_project',
  /** Uploading files to AI projects is closed on this deployment (AI_FILE_UPLOADS). */
  'ai_file_uploads_unavailable',
  /** The account this request names has already been deleted. */
  'account_not_found',
  /**
   * Deleting this account would leave a business with no owner. The response
   * names the businesses in `fields.businesses`.
   */
  'sole_owner',
  /** A business's type is chosen when it is created and can never change afterwards. */
  'business_type_locked',
  /**
   * Rider settlement was removed from CenoX (its records are kept). Not
   * worth retrying: the endpoint is gone for good, not down.
   */
  'rider_settlement_removed',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

export interface ApiErrorBody {
  error: {
    code: ErrorCode;
    message: string;
    /** Field-level messages, keyed by dotted path. Present on validation_error. */
    fields?: Record<string, string[]>;
    requestId?: string;
  };
}

export function isApiErrorBody(value: unknown): value is ApiErrorBody {
  return (
    typeof value === 'object' &&
    value !== null &&
    'error' in value &&
    typeof (value as ApiErrorBody).error?.code === 'string'
  );
}
